import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronDown, MapPin, Clock } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { EventDetailModal } from "../components/EventDetailModal";
import { api } from "../lib/api";
import { LINKS } from "../lib/constants";
import { monthShort, dayNum, timeRange } from "../lib/date";

const HERO_IMG =
  "https://customer-assets-lxgj4vgw.emergentagent.net/job_816e5609-f223-481b-bff1-206e55e7f8e0/artifacts/1pb4u4wd_IMG_2513.webp";
const TEAM_IMG = "/team.jpg";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};

export default function Home() {
  const [events, setEvents] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/events/upcoming?limit=3").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-paper" data-testid="home-page">
      <Navbar />

      {/* HERO */}
      <section className="relative h-screen min-h-[640px] w-full overflow-hidden flex items-end">
        <video
          src="/videos/hero-bg.mp4"
          poster="/videos/hero-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
          data-testid="hero-video"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <img
          src={HERO_IMG}
          alt="LSU students line dancing together at night on campus"
          className="hidden"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/40 to-ink/30" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-28 w-full">
          <motion.p
            initial="hidden" animate="show" custom={0} variants={fadeUp}
            className="font-sans text-paper/80 text-xs md:text-sm tracking-[0.28em] uppercase mb-6"
            data-testid="hero-eyebrow"
          >
            LSU Line Dancing Service Organization
          </motion.p>
          <motion.h1
            initial="hidden" animate="show" custom={1} variants={fadeUp}
            className="font-serif text-paper font-black tracking-tight leading-[0.95] text-6xl sm:text-7xl lg:text-8xl"
            data-testid="hero-headline"
          >
            Never stop<br />moving.
          </motion.h1>
          <motion.p
            initial="hidden" animate="show" custom={2} variants={fadeUp}
            className="font-sans text-paper/80 text-base md:text-xl max-w-2xl mt-8 leading-relaxed"
          >
            Tiger Trailride brings LSU students together through line dancing, service, culture, and
            opportunities that move people forward.
          </motion.p>
          <motion.div
            initial="hidden" animate="show" custom={3} variants={fadeUp}
            className="flex flex-col sm:flex-row items-start sm:items-center gap-5 mt-10"
          >
            <Link
              to="/events"
              data-testid="hero-find-event"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-trailride text-white font-sans font-medium hover:bg-trailride-dark hover:-translate-y-0.5 transition-all duration-300"
            >
              Find an Event <ArrowRight size={18} />
            </Link>
            <Link
              to="/get-involved"
              data-testid="hero-partner"
              className="inline-flex items-center gap-1.5 font-sans font-medium text-paper hover:gap-2.5 transition-all border-b border-paper/40 pb-0.5"
            >
              Partner With Us <ArrowUpRight size={16} />
            </Link>
          </motion.div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-paper/60 animate-bounce">
          <ChevronDown size={26} />
        </div>
      </section>

      {/* UP NEXT */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32" data-testid="up-next-section">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <p className="font-sans text-trailride text-xs tracking-[0.24em] uppercase font-semibold mb-4">
              Up Next
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-ink tracking-tight">
              Show up. Move together.
            </h2>
          </div>
          <Link
            to="/events"
            data-testid="up-next-all-events"
            className="inline-flex items-center gap-1.5 font-sans font-medium text-ink hover:text-trailride hover:gap-2.5 transition-all whitespace-nowrap"
          >
            All events <ArrowRight size={16} />
          </Link>
        </div>

        {events.length === 0 ? (
          <p className="font-sans text-ink/50">
            More is on the way. Follow us on{" "}
            <a href={LINKS.instagram} className="text-trailride underline" target="_blank" rel="noreferrer">
              Instagram
            </a>{" "}
            for the latest.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {events.map((event, i) => (
              <motion.div
                key={event.id}
                initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
                custom={i} variants={fadeUp}
                onClick={() => setActive(event)}
                data-testid={`up-next-card-${event.id}`}
                className="group flex flex-col bg-paper border border-ink/10 hover:border-ink/40 transition-colors duration-300 cursor-pointer"
              >
                {event.image_url && (
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={event.image_url}
                      alt={event.title}
                      loading="lazy"
                      className="w-full h-full object-cover grayscale-img group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                )}
                <div className="p-6 md:p-7 flex flex-col flex-1">
                  <div className="flex items-baseline gap-2 text-trailride mb-4">
                    <span className="font-sans font-semibold text-sm tracking-widest uppercase">
                      {monthShort(event.start)}
                    </span>
                    <span className="font-serif font-bold text-2xl leading-none">{dayNum(event.start)}</span>
                  </div>
                  <h3 className="font-serif text-xl md:text-2xl font-semibold text-ink leading-snug mb-3">
                    {event.title}
                  </h3>
                  <div className="font-sans text-sm text-ink/60 space-y-1.5 mb-5">
                    <p className="flex items-center gap-2"><Clock size={14} /> {timeRange(event.start, event.end)}</p>
                    <p className="flex items-center gap-2"><MapPin size={14} /> {event.location}</p>
                  </div>
                  <p className="font-sans text-sm text-ink/70 leading-relaxed line-clamp-2 mb-6">
                    {event.description}
                  </p>
                  {event.tigerlink_url && (
                    <a
                      href={event.tigerlink_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`up-next-rsvp-${event.id}`}
                      className="mt-auto inline-flex items-center gap-1.5 font-sans text-sm font-semibold text-trailride hover:gap-2.5 transition-all"
                    >
                      RSVP on TigerLink <ArrowUpRight size={16} />
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* SPLIT: SOUL / SERVICE */}
      <section className="bg-ink text-paper" data-testid="soul-service-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32">
          <h2 className="font-serif text-5xl md:text-7xl font-black tracking-tight leading-[0.95] mb-16 md:mb-24">
            Never stop <span className="text-trailride" style={{ color: "#9a6bbd" }}>moving.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20">
            <div>
              <p className="font-sans text-xs tracking-[0.24em] uppercase text-paper/50 mb-5">Moving with soul</p>
              <p className="font-serif text-2xl md:text-3xl leading-snug text-paper">
                Line dancing, culture, music, belonging, and community.
              </p>
              <p className="font-sans text-paper/60 mt-6 leading-relaxed">
                We move together on the floor and off it — building a space where every LSU student can
                show up as themselves and leave feeling connected.
              </p>
            </div>
            <div>
              <p className="font-sans text-xs tracking-[0.24em] uppercase text-paper/50 mb-5">Moving in service</p>
              <p className="font-serif text-2xl md:text-3xl leading-snug text-paper">
                Volunteering, student success, professional development, and campus impact.
              </p>
              <p className="font-sans text-paper/60 mt-6 leading-relaxed">
                Soul &amp; Service means turning energy into action — giving back to Baton Rouge and
                helping each other move forward.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* IMPACT STRIP */}
      <section className="grid grid-cols-1 md:grid-cols-3" data-testid="impact-strip">
        <div className="relative aspect-[4/3] md:aspect-auto md:min-h-[420px] overflow-hidden">
          <img src="https://customer-assets-4nw71qhi.emergentagent.net/job_soul-service/artifacts/j7mxw69g_FE850A9F-F21D-4215-9ABE-CDE5A13EB6EA_1_102_o.webp" alt="Tiger Trailride line dancing on the floor" loading="lazy" className="w-full h-full object-cover grayscale-img" />
        </div>
        <div className="relative aspect-[4/3] md:aspect-auto md:min-h-[420px] overflow-hidden">
          <img src="https://customer-assets-4nw71qhi.emergentagent.net/job_soul-service/artifacts/fzoc2w5g_57940EAD-F6EA-41BB-B20B-00D5B5D91237_1_106_c.jpeg" alt="Members dancing with fans" loading="lazy" className="w-full h-full object-cover grayscale-img" />
        </div>
        <div className="relative aspect-[4/3] md:aspect-auto md:min-h-[420px] overflow-hidden">
          <img src="https://customer-assets-4nw71qhi.emergentagent.net/job_soul-service/artifacts/i99ww1fn_08FBC00A-6037-4059-8C87-1557B4F3F7AF_1_102_o.jpeg" alt="Fierce line dancing crowd" loading="lazy" className="w-full h-full object-cover grayscale-img" />
        </div>
      </section>

      {/* PARTNERSHIP CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32 text-center md:text-left" data-testid="partner-cta">
        <div className="md:flex md:items-end md:justify-between gap-10">
          <h2 className="font-serif text-4xl md:text-6xl font-bold tracking-tight text-ink leading-tight max-w-2xl">
            Want to build something meaningful with us?
          </h2>
          <Link
            to="/get-involved"
            data-testid="partner-cta-button"
            className="inline-flex items-center gap-2 mt-8 md:mt-0 px-8 py-4 rounded-full bg-trailride text-white font-sans font-medium hover:bg-trailride-dark hover:-translate-y-0.5 transition-all duration-300 whitespace-nowrap"
          >
            Partner With Us <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <Footer />
      <EventDetailModal event={active} open={!!active} onClose={() => setActive(null)} />
    </div>
  );
}
